import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './http'
import { qk } from './queryKeys'
import { tokenStore } from '../auth/tokenStore'
import type { Locale } from '../i18n'
import type { AuthUser, ChangeEmailRequest, ChangePasswordRequest, ForgotPasswordRequest, LoginRequest, LoginResponse, MeResponse, MfaDisableRequest, MfaEnableRequest, MfaEnableResponse, MfaSetup, MfaStatus, ResetPasswordRequest, UpdateProfileRequest } from './types'

export const authApi = {
  login: (b: LoginRequest) => apiFetch<LoginResponse>('/auth/login', { method: 'POST', body: b, auth: false }),
  me: (signal?: AbortSignal) => apiFetch<MeResponse>('/auth/me', { signal }),
  forgotPassword: (b: ForgotPasswordRequest) => apiFetch<void>('/auth/forgot-password', { method: 'POST', body: b, auth: false }),
  resetPassword: (b: ResetPasswordRequest) => apiFetch<void>('/auth/reset-password', { method: 'POST', body: b, auth: false }),
  changePassword: (b: ChangePasswordRequest) => apiFetch<void>('/auth/change-password', { method: 'POST', body: b }),
  changeEmail: (b: ChangeEmailRequest) => apiFetch<AuthUser>('/auth/change-email', { method: 'POST', body: b }),
  updateProfile: (b: UpdateProfileRequest) => apiFetch<AuthUser>('/auth/profile', { method: 'PUT', body: b }),
  avatar: (signal?: AbortSignal) => apiFetch<Blob>('/auth/avatar', { signal, as: 'blob' }),
  setAvatar: (image: Blob) => apiFetch<AuthUser>('/auth/avatar', { method: 'PUT', body: image }),
  removeAvatar: () => apiFetch<AuthUser>('/auth/avatar', { method: 'DELETE' }),
  setLocale: (locale: Locale | null) => apiFetch<{ locale: Locale | null; effectiveLocale: Locale }>('/auth/me/locale', { method: 'PUT', body: { locale } }),
  mfaStatus: (signal?: AbortSignal) => apiFetch<MfaStatus>('/auth/mfa', { signal }),
  mfaSetup: () => apiFetch<MfaSetup>('/auth/mfa/setup', { method: 'POST' }),
  mfaEnable: (b: MfaEnableRequest) => apiFetch<MfaEnableResponse>('/auth/mfa/enable', { method: 'POST', body: b }),
  mfaDisable: (b: MfaDisableRequest) => apiFetch<void>('/auth/mfa/disable', { method: 'POST', body: b }),
}

export const authQueries = {
  me: () => queryOptions({ queryKey: qk.auth.me, queryFn: ({ signal }) => authApi.me(signal), staleTime: 30_000 }),
  mfa: () => queryOptions({ queryKey: qk.auth.mfa, queryFn: ({ signal }) => authApi.mfaStatus(signal), staleTime: 30_000 }),
}

export function useMfaSetup() {
  return useMutation({ mutationFn: authApi.mfaSetup })
}

// Enabling hands back the one-time recovery codes; the status query is refreshed by the card once the
// user has confirmed they saved them, so the codes panel isn't unmounted out from under them.
export function useMfaEnable() {
  return useMutation({ mutationFn: authApi.mfaEnable })
}

export function useMfaDisable() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: authApi.mfaDisable, onSuccess: () => qc.invalidateQueries({ queryKey: qk.auth.mfa }) })
}

export function useChangePassword() {
  return useMutation({ mutationFn: authApi.changePassword })
}

export function useChangeEmail() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: authApi.changeEmail, onSuccess: () => qc.invalidateQueries({ queryKey: qk.auth.me }) })
}

// Updates the header badge immediately — unlike email/password, the current user's display name
// is read from the in-memory session (set at login), not re-fetched from `/auth/me`.
export function useUpdateProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: (user) => {
      const session = tokenStore.getSnapshot()
      if (session.user) tokenStore.updateUser({ ...session.user, displayName: user.displayName })
      void qc.invalidateQueries({ queryKey: qk.auth.me })
    },
  })
}

// Image requests need the Bearer header, so the picture is fetched as a blob and shown via an object URL.
// The URL lives in the query cache, keyed on `avatarUpdatedAt` so a new upload busts it. It is a ~20 KB
// thumbnail and there is one per upload, so it is never revoked.
export function useAvatarUrl(): string | null {
  const me = useQuery(authQueries.me())
  const version = me.data?.avatarUpdatedAt ?? null
  const image = useQuery({
    queryKey: [...qk.auth.avatar, version],
    queryFn: async ({ signal }) => URL.createObjectURL(await authApi.avatar(signal)),
    enabled: version !== null,
    staleTime: Infinity,
  })
  return version === null ? null : (image.data ?? null)
}

function useAvatarMutation<V>(fn: (v: V) => Promise<AuthUser>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: qk.auth.me }) })
}

export const useSetAvatar = () => useAvatarMutation(authApi.setAvatar)
export const useRemoveAvatar = () => useAvatarMutation(() => authApi.removeAvatar())

export type { AuthUser }
